import { useAtom } from 'jotai/index';
import { atom } from 'jotai';
import { IPagination, IProduct } from '@shared/types.ts';
import { useCallback } from 'react';
import { useFavoriteProductsService } from './use-favorite-products.service.ts';

export const favoriteProductsAtom = atom<IPagination<IProduct>>({
  data: [],
  meta: { current_page: 1, last_page: 1 },
});

export function useFavoriteProductsStore({
  isUseCache,
}: {
  isUseCache: boolean;
}) {
  const [data, setData] = useAtom(favoriteProductsAtom);

  const init = useCallback(() => {
    if (isUseCache) {
      const storedProducts = readCache();
      setData(storedProducts);
    }
  }, [data, isUseCache]);

  const service = useFavoriteProductsService({
    onUpdate: (products) => setData(products),
  });

  const key = 'local_favorite_products_list';

  const invertProduct = useCallback(
    (product: IProduct) => {
      const isProductInCart = data.data.find(({ id }) => product.id === id);
      if (isProductInCart) {
        const updatedList = data.data.filter((prevProduct) => {
          return prevProduct.id !== product.id;
        });
        setData((state) => ({
          data: updatedList,
          meta: state.meta,
        }));
        if (isUseCache) {
          updateCache({
            data: updatedList,
            meta: data.meta,
          });
        }

        const deleteProduct = service.data?.data.find(
          (favoriteProduct) => favoriteProduct.product.id === product.id
        );

        if (deleteProduct && !isUseCache) {
          service.removeProduct.trigger({ productId: deleteProduct.id });
        }
      } else {
        setData((state) => ({
          data: [...state.data, product],
          meta: state.meta,
        }));
        if (!isUseCache) {
          service.updateProduct.trigger({ productId: product.id });
        } else {
          updateCache({
            data: [...data.data, product],
            meta: data.meta,
          });
        }
      }
    },
    [data, isUseCache]
  );

  const addProducts = useCallback(
    (products: IProduct[]) => {
      setData((state) => ({
        data: [...state.data, ...products],
        meta: state.meta,
      }));

      if (isUseCache) {
        updateCache({
          data: [...data.data, ...products],
          meta: data.meta,
        });
      }
    },
    [data, isUseCache]
  );

  const clearCachedData = useCallback(() => {
    clearCache();
  }, []);

  function updateCache(products: IPagination<IProduct>) {
    if (products.data && Array.isArray(products.data)) {
      localStorage.setItem(key, JSON.stringify(products));
    }
  }

  function readCache(): IPagination<IProduct> {
    const value = localStorage.getItem(key);
    if (!value)
      return {
        data: [],
        meta: { current_page: 1, last_page: 1 },
      };
    return JSON.parse(value);
  }

  function clearCache(): void {
    localStorage.removeItem(key);
  }

  const validateRemove = useCallback(() => {
    const storedProducts = readCache();
    const product_ids = storedProducts.data.map((product) => product.id);
    if (product_ids.length > 0) service.updateProducts.trigger({ product_ids });
  }, []);

  const productIds = data.data.map((product) => product.id);
  const isFavorite = (productId: number) => productIds.includes(productId);

  return {
    init,
    data: data.data,
    invertProduct,
    addProducts,
    clearCachedData,
    isFavorite,
    validateRemove,
    isLoading: service.isLoading,
    onLoadMore: () => service.onLoadMore(data.meta.current_page + 1),
    hasNextPage: data.meta.current_page < data.meta.last_page,
  };
}
